import { Link } from "react-router-dom";
import { ArrowRight, Printer, Sparkles, Gift } from "lucide-react";
import { products } from "../data";
import { ProductCard, ServiceCard } from "../components/Cards";
import { DeluxBoxesSection } from "../components/DeluxBoxesSection";
import { ScrollReveal, StaggerContainer, StaggerItem } from "../components/ScrollReveal";
import { motion } from "framer-motion";
import Counter from "../components/Counter";

const Home = () => {
  const popularProducts = products.filter(p => p.popular).slice(0, 4);
  const newProducts = products.filter(p => p.newArrival).slice(0, 4);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[60vh] lg:min-h-[85vh]">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="flex flex-col justify-center items-center lg:items-start text-center lg:text-left py-16 lg:py-24 px-4 sm:px-6 lg:pl-8 lg:pr-20 xl:pl-[calc((100vw-1280px)/2+2rem)] space-y-10 text-white z-10 bg-blue-700"
          >
            <div className="space-y-6">
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-semibold tracking-tighter leading-[0.9] mb-4"
              >
                Bring dine <br />
                <span className="text-white">idéer til live</span>
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-xl md:text-2xl text-blue-50 max-w-lg leading-relaxed opacity-90"
              >
                Vi skaber personlige og funktionelle 3D-printede produkter med fokus på kvalitet og innovation.
              </motion.p>
            </div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-5 w-full sm:w-auto pt-4"
            >
              <Link to="/produkter" className="btn-primary text-lg md:text-base lg:text-lg px-10 md:px-8 lg:px-10 py-4 md:py-3 lg:py-4 bg-orange-600 hover:bg-orange-500 text-white rounded-none shadow-2xl shadow-orange-950/40 transition-all transform hover:-translate-y-1">
                Se produkter <ArrowRight size={20} className="ml-2 md:w-4 md:h-4 lg:w-5 lg:h-5" />
              </Link>
              <Link to="/ydelser" className="btn-secondary text-lg md:text-base lg:text-lg px-10 md:px-8 lg:px-10 py-4 md:py-3 lg:py-4 bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-700 rounded-none transition-all transform hover:-translate-y-1">
                Specialopgaver
              </Link>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="grid grid-cols-1 sm:grid-cols-3 gap-8 pt-16 w-full"
            >
              <div className="flex flex-col items-center lg:items-start gap-3">
                <div className="p-3 bg-white/10 rounded-full text-white">
                  <Printer size={24} />
                </div>
                <div className="text-center lg:text-left">
                  <h3 className="font-semibold text-base">Høj Kvalitet</h3>
                  <p className="text-blue-100/70 text-sm leading-snug">Præcision i hver detalje.</p>
                </div>
              </div>
              <div className="flex flex-col items-center lg:items-start gap-3">
                <div className="p-3 bg-white/10 rounded-full text-white">
                  <Sparkles size={24} />
                </div>
                <div className="text-center lg:text-left">
                  <h3 className="font-semibold text-base">Unikt Design</h3>
                  <p className="text-blue-100/70 text-sm leading-snug">Skabt specifikt til dig.</p>
                </div>
              </div>
              <div className="flex flex-col items-center lg:items-start gap-3">
                <div className="p-3 bg-white/10 rounded-full text-white">
                  <Gift size={24} />
                </div>
                <div className="text-center lg:text-left">
                  <h3 className="font-semibold text-base">Perfekte Gaver</h3>
                  <p className="text-blue-100/70 text-sm leading-snug">Personlige og uforglemmelige.</p>
                </div>
              </div>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1 }}
              className="flex gap-16 pt-12 border-t border-white/10 w-full justify-center lg:justify-start"
            >
              <div>
                <div className="text-4xl font-semibold">
                  <Counter end={500} suffix="+" />
                </div>
                <div className="text-[10px] uppercase tracking-widest text-blue-200/60 font-bold mt-1">Projekter</div>
              </div>
              <div>
                <div className="text-4xl font-semibold">
                  100%
                </div>
                <div className="text-[10px] uppercase tracking-widest text-blue-200/60 font-bold mt-1">Dansk Design</div>
              </div>
            </motion.div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, ease: "easeOut" }}
            className="relative h-[50vh] lg:h-auto overflow-hidden bg-slate-100"
          >
            <div className="absolute inset-0 z-10">
              <img 
                src="/images/hero-3d-illustration.png" 
                alt="3D Printed Innovation" 
                className="w-full h-full object-cover grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white via-white/20 to-transparent hidden lg:block opacity-40 z-30"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DELUX Kasser Section */}
      <DeluxBoxesSection />

      {/* Popular Products */}
      <section className="py-12 md:py-20 bg-zinc-950">
        <div className="container-custom">
          <ScrollReveal className="flex justify-between items-end mb-10">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="h-[2px] w-10 bg-orange-600"></div>
                <span className="text-orange-500 font-black uppercase tracking-[0.2em] text-[10px]">Premium Selection</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-white uppercase">Udvalgte produkter</h2>
              <p className="text-zinc-500 mt-2 max-w-lg border-l-2 border-zinc-800 pl-4">Vores mest populære designs, håndplukket til dig.</p>
            </div>
            <Link to="/produkter" className="text-zinc-400 hover:text-orange-500 font-bold transition-all hidden sm:flex items-center gap-2 uppercase text-xs tracking-widest">
              Se alle produkter <ArrowRight size={16} />
            </Link>
          </ScrollReveal>
          
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {popularProducts.map(product => (
              <StaggerItem key={product.id}>
                <div className="h-full group">
                  <ProductCard product={product} showBorder={false} />
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* About Section */}
      <section className="bg-slate-50 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="relative group lg:order-1 order-2 h-[400px] lg:h-full min-h-[400px] lg:min-h-[700px] overflow-hidden"
          >
            <img 
              src="/images/workshop-creators.png" 
              alt="Hans Marius & Joan Edvør i værkstedet" 
              className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
            />
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="py-20 lg:py-32 px-4 sm:px-6 lg:pr-8 lg:pl-20 xl:pr-[calc((100vw-1280px)/2+2rem)] space-y-10 lg:order-2 order-1 bg-white text-zinc-900"
          >
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <div className="h-[1px] w-8 bg-zinc-300"></div>
                <span className="text-zinc-500 font-medium uppercase tracking-[0.2em] text-[10px]">Om Hj3ddesign</span>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight">
                Mød Hans Marius & Joan Edvør
              </h2>
              <div className="space-y-4">
                <p className="text-lg md:text-xl text-zinc-600 leading-relaxed max-w-xl">
                  Vi brænder for at gøre dine idéer til virkelighed. Hos Hj3ddesign skaber vi alt fra unikke figurer til funktionelle brugsgenstande med præcision og omtanke.
                </p>
                <p className="text-lg md:text-xl text-zinc-600 leading-relaxed max-w-xl">
                  Vi leverer ikke bare et print, men et stykke håndværk skabt med kærlighed til detaljen.
                </p>
              </div>
              <p className="text-xl font-medium italic text-orange-600">
                Lad os sammen skabe noget unikt.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/om-os" className="btn-primary text-lg px-10 py-4 bg-zinc-900 text-white hover:bg-zinc-800 rounded-none inline-flex items-center justify-center transition-all shadow-xl shadow-zinc-200">
                Læs mere om os <ArrowRight size={20} className="ml-2" />
              </Link>
              <Link to="/kontakt" className="btn-secondary text-lg px-10 py-4 bg-transparent border-2 border-zinc-200 text-zinc-900 hover:bg-zinc-50 rounded-none inline-flex items-center justify-center transition-all">
                Kontakt os
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-12 md:py-20 bg-zinc-50">
        <div className="container-custom">
          <ScrollReveal className="flex flex-col md:flex-row justify-between items-start md:items-end mb-10 gap-4">
            <div>
              <span className="text-orange-600 font-bold uppercase tracking-widest text-sm">Nyheder</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight mt-2 text-zinc-900">Nye ankomster</h2>
            </div>
            <Link to="/produkter" className="group flex items-center gap-2 text-zinc-500 font-semibold hover:text-zinc-900 transition-colors">
              Udforsk hele sortimentet
              <div className="p-2 bg-zinc-200 rounded-full group-hover:bg-orange-600 group-hover:text-white transition-all">
                <ArrowRight size={18} />
              </div>
            </Link>
          </ScrollReveal>
          
          <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {newProducts.map(product => (
              <StaggerItem key={product.id}>
                <div className="h-full">
                  <ProductCard product={product} showBorder={false} />
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>
    </div>
  );
};

export default Home;
